import { CustomerContactPersonModelType } from '@teinor/erp/types/company/customer/customerContactPerson'
import { useCallback, useEffect, useState } from 'react'
import { MediumModalCreateEdit } from '../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../baseComponents/ModalsLayouts/types'
import { permissionCheck } from '../../../../../baseComponents/PermissionChecker/function'
import { RowData } from '../../../../../baseComponents/TwinTable/types'
import { FormRender } from '../../../../../forms/FormRenderer/types'
import { twinFetchPostJSON } from '../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import { valueOrDefaultValue } from '../../../../../utils/globals/valueOrDefaultValue'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { returnClassOfSpanTemplate } from '../../../../../utils/globals/tailwind'
import { ExtraModalProps } from '../../../../../baseComponents/TwinTable/BaseScreenTable'
import { Link } from 'react-router-dom'
import { useCustomerParsedLinkMemo } from '../../../../../utils/hooks/customer/useCustomerTypesDict'
import TwinTrans from '../../../../../baseComponents/TwinTrans'

interface CustomerContactPersonModelTypeExtends extends CustomerContactPersonModelType {
    customerContactPersonAccesses: string[]
    link: string
}

type ModalEditCustomerContactPersonProps = & ModalEditComponentProps & ExtraModalProps & {}

const ModalEditCustomerContactPerson: React.FC<ModalEditCustomerContactPersonProps> = ({ userPermissions, allRowData, extraModalData, ...rest }) => {
    const { t } = useTwinTranslation()
    const { data } = useModalEditCustomerContactPersonLogic({ allRowData })
    const accesses: TwinDictionary = { 'task_bag_of_hours': { value: 'task_bag_of_hours', name: t('bagOfHours', 'Bolsa de horas') } }
    const { parsedLinkMemo } = useCustomerParsedLinkMemo({ id: allRowData?.Customer?.id, customer_type: allRowData?.Customer?.customer_type })

    const fields: FormRender<CustomerContactPersonModelTypeExtends> = [
        {
            cols: 2,
            elements: [
                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true
                },
                {
                    name: 'position',
                    label: t('position', 'Cargo'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'notes',
                    label: t('notes', 'Notas'),
                    component: 'TextArea',
                    parentClassName: returnClassOfSpanTemplate(2)
                },
                {
                    name: 'id',
                    component: 'InputHidden'
                },
                {
                    name: 'CustomerId',
                    component: 'InputHidden',
                    value: allRowData?.CustomerId
                },
            ],
        },
        {
            cols: 2,
            title: t('contatInformation', 'Información de contacto'),
            elements: [
                {
                    name: 'phone',
                    label: t('phone', 'Teléfono'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'mobile',
                    label: t('mobile', 'Móvil'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'email',
                    label: t('email', 'Email'),
                    component: 'InputWithLabelMargin',
                    type: 'email',
                },
            ],
        },
        {
            cols: 1,
            elements: [
                {
                    name: 'customerContactPersonAccesses',
                    component: 'MultipleCheckboxSelector',
                    subtitles: {
                        subtitleOptions: t('accesses', 'Accesos'),
                        subtitleSelectedOptions: t('selectedAccesses', 'Accesos seleccionados')
                    },
                    items: accesses  || {},
                    defaultValue: data?.customerContactPersonAccesses || [],
                },
            ]
        }
    ]
    if (extraModalData?.showLink) {
        fields[0]?.elements.unshift({
            //@ts-ignore
            name: '',
            component: 'Custom',
            render: () => {
                return <Link className={'cursor-pointer mb-20 text-green-21 hover:text-gray-51 ' + returnClassOfSpanTemplate(2)} to={parsedLinkMemo}>
                    <TwinTrans transKey={'goToCustomer'}>Ir al cliente</TwinTrans>
                </Link>
            }
        })
    }
    if (allRowData && userPermissions) {
        if (!data) {
            return null
        }
        const parsedFields = valueOrDefaultValue(fields, userPermissions, allRowData)
        return (<MediumModalCreateEdit fields={parsedFields} url={'/api/app/customer/customerContactPerson/updateCustomerContactPerson'} translations={{ title: t('editContactPerson', 'Editar Persona de contacto'), button: t('save', 'Guardar') }} haveButtonPermissions={permissionCheck(userPermissions, 'update')} {...rest} />
        )
    }

    return null
}

interface ModalEditCustomerContactPersonLogicProps {
    allRowData?: RowData
}

const useModalEditCustomerContactPersonLogic = ({allRowData }: ModalEditCustomerContactPersonLogicProps) => {
    const [data, setData] = useState<CustomerContactPersonModelTypeExtends | null>(null)
    
    const getCustomerContactPersonData = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/customerContactPerson/getCustomerContactPersonForDetail', { id: allRowData?.id })
        if (result) {
            const accessesParsed: string[] = []
            if (result.CustomerContactPersonAccesses) {
                for (const element of result.CustomerContactPersonAccesses) {
                    accessesParsed.push(element.access_key)
                }
            }
            setData({ ...result, customerContactPersonAccesses: accessesParsed})
        }
    }, [setData, allRowData?.id])

    useEffect(() => {
        getCustomerContactPersonData()
    }, [getCustomerContactPersonData])

    return {data}
}

export default ModalEditCustomerContactPerson